import { useEffect, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import logger from '@/utils/logger';

/**
 * 支持的布局类型
 */
export type LayoutType = 'default' | 'layout2' | 'layout3' | 'layout4';

/**
 * 布局来源：路径参数还是查询参数
 */
export type LayoutSource = 'path' | 'query' | 'none';

/**
 * 布局类型到 CSS 类名的映射
 */
const LAYOUT_CLASS_MAP: Record<LayoutType, string | null> = {
  default: null,
  layout2: 'layout2-theme',
  layout3: 'layout3-theme',
  layout4: 'layout4-theme',
};

/**
 * 所有布局相关的 CSS 类名
 */
const ALL_LAYOUT_CLASSES = ['layout2-theme', 'layout3-theme', 'layout4-theme'];

/**
 * 解析布局类型（支持路径参数和查询参数）
 * 优先级：路径参数 > 查询参数 > 默认值
 */
function parseLayoutType(
  layoutParam: string | undefined,
  searchParams: URLSearchParams
): { layoutType: LayoutType; source: LayoutSource } {
  // 1. 优先检查路径参数（如 /scene/elder/layout4）
  if (layoutParam) {
    const normalized = layoutParam.toLowerCase();
    if (normalized === 'layout2') return { layoutType: 'layout2', source: 'path' };
    if (normalized === 'layout3') return { layoutType: 'layout3', source: 'path' };
    if (normalized === 'layout4') return { layoutType: 'layout4', source: 'path' };
  }

  // 2. 其次检查查询参数（如 ?layout4=true）
  if (searchParams.get('layout4') === 'true') return { layoutType: 'layout4', source: 'query' };
  if (searchParams.get('layout3') === 'true') return { layoutType: 'layout3', source: 'query' };
  if (searchParams.get('layout2') === 'true') return { layoutType: 'layout2', source: 'query' };

  // 3. 默认布局
  return { layoutType: 'default', source: 'none' };
}

/**
 * 统一的布局检测 Hook
 * 同时支持路径参数（/scene/elder/layout4）和查询参数（?layout4=true）
 */
export function useLayout() {
  const { layoutType: layoutParam } = useParams<{ layoutType?: string }>();
  const location = useLocation();

  const { layoutType, source } = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return parseLayoutType(layoutParam, searchParams);
  }, [layoutParam, location.search]);

  // 应用布局 CSS 类
  useEffect(() => {
    ALL_LAYOUT_CLASSES.forEach((cls) => {
      document.documentElement.classList.remove(cls);
    });

    const layoutClass = LAYOUT_CLASS_MAP[layoutType];
    if (layoutClass) {
      document.documentElement.classList.add(layoutClass);
      logger.info(`[Layout] 应用布局: ${layoutType}, 来源: ${source}`);
    }
  }, [layoutType, source]);

  return {
    layoutType,
    layoutSource: source,
    isDefault: layoutType === 'default',
    isLayout2: layoutType === 'layout2',
    isLayout3: layoutType === 'layout3',
    isLayout4: layoutType === 'layout4',
    chatLayoutMode: layoutType === 'layout4' ? 'split' : ('default' as 'split' | 'default'),
  };
}

/**
 * 从当前 URL 获取布局信息（非 Hook 版本）
 */
export function getLayoutInfoFromURL(): { layoutType: LayoutType; source: LayoutSource } {
  const url = new URL(window.location.href);

  // 检查路径参数
  const pathMatch = url.pathname.match(/\/scene\/[^/]+\/(layout[234])/i);
  if (pathMatch) {
    const layout = pathMatch[1].toLowerCase();
    if (layout === 'layout2') return { layoutType: 'layout2', source: 'path' };
    if (layout === 'layout3') return { layoutType: 'layout3', source: 'path' };
    if (layout === 'layout4') return { layoutType: 'layout4', source: 'path' };
  }

  // 检查查询参数
  if (url.searchParams.get('layout4') === 'true') return { layoutType: 'layout4', source: 'query' };
  if (url.searchParams.get('layout3') === 'true') return { layoutType: 'layout3', source: 'query' };
  if (url.searchParams.get('layout2') === 'true') return { layoutType: 'layout2', source: 'query' };

  return { layoutType: 'default', source: 'none' };
}

/**
 * 构建保留布局参数的 URL
 * 根据原有来源决定使用路径参数还是查询参数
 */
export function buildURLWithLayout(
  basePath: string,
  sceneId: string | null,
  additionalParams?: URLSearchParams
): string {
  const { layoutType, source } = getLayoutInfoFromURL();
  const params = additionalParams ? new URLSearchParams(additionalParams) : new URLSearchParams();

  // 移除布局相关的查询参数（避免重复）
  params.delete('layout2');
  params.delete('layout3');
  params.delete('layout4');

  let finalPath = basePath;

  if (layoutType !== 'default') {
    if (source === 'path' && sceneId) {
      // 原来是路径参数，继续使用路径参数
      finalPath = `/scene/${encodeURIComponent(sceneId)}/${layoutType}`;
    } else {
      // 原来是查询参数或首页，使用查询参数
      params.set(layoutType, 'true');
    }
  }

  const queryString = params.toString();
  return queryString ? `${finalPath}?${queryString}` : finalPath;
}
